import React, { useState } from 'react';
import { Apple, AlertTriangle, ChevronDown, ChevronUp, Utensils, Scale, Pill, Clock, Sun, BookOpen, HeartHandshake } from 'lucide-react';
import { ContentGateWrapper, useContentGate } from '../components/ContentGateWrapper';

/**
 * EatingWellPage - Member-only, blurred content
 * Content based ONLY on provided source material
 * Collapsible sections with calm, non-medical tone
 */

const EATING_SECTIONS = [
  {
    id: 'eating-enough',
    title: 'Eating Enough',
    icon: Scale,
    content: `For people living with CLL, maintaining adequate nutrition is often more important than following restrictive diets. Weight loss can sometimes be a concern, particularly during or after treatment.

The goal is generally to eat enough to maintain your weight and energy levels. This doesn't mean eating perfectly — it means eating sufficiently.

If you're finding it difficult to eat enough, small changes like adding extra portions or calorie-dense foods may help. Your healthcare team can provide guidance specific to your situation.`
  },
  {
    id: 'food-over-supplements',
    title: 'Food Over Supplements',
    icon: Apple,
    content: `Where possible, getting nutrients from food is generally preferred over relying on supplements. Whole foods contain combinations of nutrients that work together in ways that supplements may not replicate.

This doesn't mean supplements are never appropriate — sometimes they're necessary. But the foundation of good nutrition is usually a varied diet that includes different types of foods.

Before taking any supplements, discuss them with your healthcare team. Some supplements can interact with medications or affect blood test results.`
  },
  {
    id: 'variety',
    title: 'Variety of Foods',
    icon: Utensils,
    content: `Eating a variety of foods helps ensure you get a range of nutrients. This includes:

• Different colored fruits and vegetables
• Various protein sources (if appropriate for you)
• Whole grains when tolerated
• Dairy or alternatives as needed

Variety doesn't have to mean complicated meals. Simple combinations of different food types can provide good nutritional coverage.`
  },
  {
    id: 'small-frequent-meals',
    title: 'Small Frequent Meals',
    icon: Clock,
    content: `When appetite is reduced or eating large meals feels difficult, smaller meals eaten more frequently can be helpful. This approach can:

• Feel more manageable when energy is low
• Help maintain calorie intake throughout the day
• Reduce feelings of being overwhelmed by food
• Allow for flexibility based on how you feel

There's no perfect number of meals — find a pattern that works for you.`
  },
  {
    id: 'protein-guidance',
    title: 'Protein Guidance',
    icon: Utensils,
    content: `Protein is important for maintaining muscle mass and supporting recovery. Good protein sources include:

• Meat, poultry, and fish (if consumed)
• Eggs and dairy products
• Legumes, beans, and lentils
• Nuts and seeds
• Tofu and other soy products

The right amount of protein varies by individual. If you're concerned about whether you're getting enough, a registered dietitian can assess your needs.`
  },
  {
    id: 'citrus-interactions',
    title: 'Citrus & Medication Interactions',
    icon: Pill,
    content: `Some citrus fruits, particularly grapefruit, can interact with certain medications. These interactions can affect how your body processes the medication.

If you're taking any medications, it's worth checking with your pharmacist about potential food interactions. This includes:

• Grapefruit and grapefruit juice
• Seville oranges (often used in marmalade)
• Pomelos

Other citrus fruits like regular oranges, lemons, and limes are usually not affected by the same interactions, but it's always worth confirming with your healthcare team.`
  },
  {
    id: 'fatigue-eating',
    title: 'Fatigue-Friendly Eating',
    icon: Clock,
    content: `Fatigue can make cooking and eating challenging. Some strategies that may help:

• Prepare food when energy is higher and store for later
• Keep easy-to-grab nutritious snacks accessible
• Accept help from family and friends with meal preparation
• Use convenience foods when needed — nutrition matters more than perfection
• Consider eating your main meal when energy is typically highest

There's no shame in simplifying meals during difficult periods.`
  },
  {
    id: 'vitamin-d',
    title: 'Vitamin D Discussion',
    icon: Sun,
    content: `Vitamin D is a topic often discussed in the context of CLL. Research is ongoing about its role, and there's no definitive guidance that applies to everyone.

What we do know:
• Vitamin D levels can be measured through a blood test
• Deficiency is common in the general population
• Supplementation may be appropriate for some people

Whether you should have your levels tested or take supplements is a conversation to have with your doctor. They can interpret your results in the context of your overall health and treatment plan.

Do not start vitamin D supplements without medical guidance.`
  },
  {
    id: 'evidence-based',
    title: 'Evidence-Based Nutrition',
    icon: BookOpen,
    content: `There's a lot of nutrition information available, and not all of it is reliable. When evaluating nutrition claims:

• Be cautious of claims that sound too good to be true
• Look for information from reputable medical sources
• Remember that what works for one person may not work for another
• Discuss any significant dietary changes with your healthcare team

Evidence-based nutrition focuses on well-researched principles rather than trends or anecdotes.`
  },
  {
    id: 'professional-help',
    title: 'When to Seek Professional Help',
    icon: HeartHandshake,
    content: `Consider speaking with a registered dietitian or your healthcare team if:

• You're experiencing unintentional weight loss
• Eating has become very difficult or distressing
• You have questions about specific foods or supplements
• You want personalized nutrition guidance
• You're concerned about your nutritional status

A registered dietitian can provide individualized advice that takes into account your specific situation, treatment, and preferences.

Your healthcare team can provide referrals to nutrition services when appropriate.`
  }
];

const CollapsibleSection = ({ section, isExpanded, onToggle }) => {
  const Icon = section.icon;
  
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-5 hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-teal-50 rounded-lg">
            <Icon className="h-5 w-5 text-teal-600" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 text-left">
            {section.title}
          </h3>
        </div>
        {isExpanded ? (
          <ChevronUp className="h-5 w-5 text-slate-400" />
        ) : (
          <ChevronDown className="h-5 w-5 text-slate-400" />
        )}
      </button>
      
      {isExpanded && (
        <div className="px-5 pb-5 pt-0">
          <div className="pl-12 border-l-2 border-teal-100">
            {section.content.split('\n\n').map((paragraph, idx) => (
              <p key={idx} className="text-slate-700 leading-relaxed mb-3 last:mb-0">
                {paragraph}
              </p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const EatingWellPage = () => {
  const { isGated } = useContentGate();
  const [expandedSections, setExpandedSections] = useState(['eating-enough']);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev =>
      prev.includes(sectionId)
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <Apple className="h-8 w-8 text-primary" />
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900">
            Eating Well With CLL
          </h1>
        </div>
        <p className="text-lg leading-relaxed text-slate-700">
          Supportive nutrition information for your journey
        </p>
      </div>

      {/* Educational Introduction */}
      <div className="bg-gradient-to-br from-teal-50 to-blue-50 border border-teal-200 rounded-2xl p-6 md:p-8">
        <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">
          About this section
        </h2>
        <div className="space-y-4 text-base md:text-lg text-slate-700 leading-relaxed">
          <p>
            This section provides general information about nutrition for people living with CLL. 
            The focus is on practical, supportive approaches rather than strict rules or diets.
          </p>
          <p>
            Everyone&apos;s situation is different. What works for one person may not be right for 
            another. These are starting points for conversations with your healthcare team, 
            not prescriptions.
          </p>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
        <div className="flex gap-3">
          <AlertTriangle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-lg font-semibold text-amber-900 mb-2">Important Notice</h3>
            <p className="text-amber-800 leading-relaxed">
              This information is for general education only. It does not replace personalized 
              advice from your healthcare team or a registered dietitian. Always discuss 
              significant dietary changes with your medical team, especially during treatment.
            </p>
          </div>
        </div>
      </div>

      {/* Collapsible Content Sections */}
      <ContentGateWrapper mode="page">
        <div className="space-y-4">
          {EATING_SECTIONS.map((section) => (
            <CollapsibleSection
              key={section.id}
              section={section}
              isExpanded={expandedSections.includes(section.id)}
              onToggle={() => toggleSection(section.id)}
            />
          ))}
        </div>
      </ContentGateWrapper>
    </div>
  );
};

export default EatingWellPage;
